/**
 * PdfViewer — PDF 를 캔버스로 직접 그리는 뷰어 (Mozilla pdfjs).
 *
 * 왜 iframe 이 아닌가: 안드로이드 크롬은 내장 인라인 PDF 뷰어가 없어 iframe 이
 * **빈 화면**이고, iOS(WebKit)는 Quick Look 이 **첫 페이지만** 보여준다(실사용 제보).
 * 모바일에서 이 컴포넌트가 iframe 을 대신한다 — 데스크톱은 네이티브 뷰어가
 * 더 좋아(툴바·검색) iframe 을 유지한다. 분기는 쓰는 쪽(MarketPage) 책임.
 *
 * 성능 규칙:
 *  - pdfjs(~300KB)는 **열 때 동적 로드** — 앱 본체 번들에 붙지 않는다
 *  - **보이는 페이지만 그린다** (IntersectionObserver, 위아래 800px 선렌더)
 *  - 해상도 = 컨테이너 폭 맞춤 × min(devicePixelRatio, 2) × 1.4 — 적당한 핀치
 *    줌까지 또렷하게(줌 재렌더 없이). 캔버스 한 변 4096px 상한(iOS 캔버스 한계)
 *
 * 구조: 문서 로드(effect A)와 페이지 관찰(effect B)을 나눈다 — 자리(.pdfv-page)는
 * setState 커밋 뒤에야 DOM 에 생기므로, 로드 콜백 안에서 바로 관찰을 걸면
 * 빈 목록을 잡는 레이스가 된다(실측 canvases 0).
 */
import { useEffect, useRef, useState } from "react";
import type { PDFDocumentProxy } from "pdfjs-dist";

export interface PdfViewerProps {
  /** PDF 주소 — 교차 출처면 CORS 가 열려 있거나 프록시(/agent/doc-proxy)여야 한다 */
  src: string;
  className?: string;
}

/** 렌더 배율 — 폭 맞춤보다 약간 크게 그려 핀치 줌 여유를 둔다 */
const SHARPNESS = 1.4;
const MAX_CANVAS_EDGE = 4096;

export function PdfViewer({ src, className }: PdfViewerProps) {
  const boxRef = useRef<HTMLDivElement>(null);
  const docRef = useRef<PDFDocumentProxy | null>(null);
  const [state, setState] = useState<{ pages: number; error: string }>({ pages: 0, error: "" });
  // 페이지 자리의 기본 세로 비율 — 첫 페이지를 재서 전체 자리표시자에 쓴다
  // (그리기 전 스크롤바 길이가 널뛰지 않게). A4 세로가 기본값.
  const [ratio, setRatio] = useState(1.414);

  // ── A. 문서 로드 ────────────────────────────────────────────────
  useEffect(() => {
    let dead = false;
    // 정리는 문서가 아니라 로딩 태스크로 — v6 에서 destroy 는 태스크의 것이다
    let task: { destroy: () => Promise<void> } | null = null;
    docRef.current = null;
    setState({ pages: 0, error: "" });

    (async () => {
      try {
        // legacy 빌드를 쓴다 — 기본 빌드는 Promise.withResolvers 를 써서
        // iOS 17.4 미만 사파리에서 워커가 죽는다(아이폰 미리보기 불가 제보)
        const pdfjs = (await import(
          "pdfjs-dist/legacy/build/pdf.mjs"
        )) as typeof import("pdfjs-dist");
        pdfjs.GlobalWorkerOptions.workerSrc =
          new URL("pdfjs-dist/legacy/build/pdf.worker.min.mjs", import.meta.url).toString();
        const loading = pdfjs.getDocument({ url: src });
        task = loading;
        const loaded = await loading.promise;
        if (dead) return;
        const first = await loaded.getPage(1);
        if (dead) return;
        const v1 = first.getViewport({ scale: 1 });
        docRef.current = loaded;
        setRatio(v1.height / v1.width);
        setState({ pages: loaded.numPages, error: "" });
      } catch (e) {
        if (!dead) setState({ pages: 0, error: String((e as Error)?.message || e) });
      }
    })();

    return () => {
      dead = true;
      void task?.destroy();
    };
  }, [src]);

  // ── B. 보이는 페이지만 그리기 — 자리가 DOM 에 생긴 뒤에 관찰을 건다 ──
  useEffect(() => {
    const doc = docRef.current;
    const box = boxRef.current;
    if (!doc || !box || state.pages === 0) return;
    let dead = false;

    const render = async (slot: HTMLElement) => {
      if (slot.dataset.done) return;
      slot.dataset.done = "1";
      try {
        const page = await doc.getPage(Number(slot.dataset.page));
        if (dead) return;
        const vw1 = page.getViewport({ scale: 1 });
        const width = slot.clientWidth || 320;
        const dpr = Math.min(window.devicePixelRatio || 1, 2) * SHARPNESS;
        let scale = (width / vw1.width) * dpr;
        // iOS 캔버스 한계 — 한 변이 상한을 넘으면 그 안으로 줄인다
        scale = Math.min(scale, MAX_CANVAS_EDGE / vw1.width, MAX_CANVAS_EDGE / vw1.height);
        const vp = page.getViewport({ scale });
        const canvas = document.createElement("canvas");
        canvas.width = Math.floor(vp.width);
        canvas.height = Math.floor(vp.height);
        await page.render({ canvas, canvasContext: canvas.getContext("2d")!, viewport: vp }).promise;
        if (dead) return;
        slot.style.minHeight = "";   // 자리표시자 높이 해제 — 실제 캔버스가 정한다
        slot.replaceChildren(canvas);
      } catch {
        slot.dataset.done = "";      // 실패한 페이지는 다시 보일 때 재시도
      }
    };

    const slots = [...box.querySelectorAll<HTMLElement>(".pdfv-page")];
    // 처음 두 장은 관찰을 기다리지 않고 바로 그린다 — 첫 화면이 즉시 차고,
    // IO 가 얼어 있는 환경(숨김 탭·일부 웹뷰)에서도 문서가 비어 보이지 않는다
    slots.slice(0, 2).forEach((el) => void render(el));
    const io = new IntersectionObserver((ents) => {
      for (const e of ents) if (e.isIntersecting) void render(e.target as HTMLElement);
    }, { root: box, rootMargin: "800px 0px" });
    slots.forEach((el) => io.observe(el));

    return () => {
      dead = true;
      io.disconnect();
    };
  }, [state.pages]);

  return (
    <div ref={boxRef} className={`pdfv${className ? " " + className : ""}`}>
      {state.error && (
        <div className="pdfv-note">
          문서를 그리지 못했습니다.
          <br />
          위의 '새 탭'으로 원문을 열어 주세요.
        </div>
      )}
      {!state.error && state.pages === 0 && <div className="pdfv-note">문서를 여는 중…</div>}
      {Array.from({ length: state.pages }, (_, i) => (
        <div
          key={i}
          className="pdfv-page"
          data-page={i + 1}
          // 그리기 전 자리 높이 — 첫 페이지 비율로 어림잡아 스크롤 길이를 안정시킨다
          style={{ minHeight: `${Math.round(ratio * 100)}vw` }}
        />
      ))}
    </div>
  );
}
